import React from 'react'
import moment, { Moment } from 'moment'
import classNames from 'classnames'
import { FC } from 'react'

import styles from './style.module.scss'
import * as dateUtils from '../../utils/DateUtils'

interface DayInfo {
  date: Moment
  dayOfMonth: number
  isCurrentMonth: boolean
  isToday: boolean
  isFocused: boolean
  isInLimitDate: boolean
}

interface HasEventIndex {
  index: number
  date?: Date
}

interface LimitDate {
  startDate: Date | null
  endDate: Date | null
}

interface MonthGridProps {
  date: Date
  focusedDate: Date
  viewType: string
  onDateClick: (date: Moment) => void
  hasEventIndexes?: HasEventIndex[] | null
  callViewType?: string
  limitDate?: LimitDate
}

const MonthGrid: FC<MonthGridProps> = ({
  date,
  focusedDate,
  viewType,
  onDateClick,
  hasEventIndexes = null,
  callViewType = 'calendar',
  limitDate = dateUtils.LIMIT_DATE,
}) => {
  const isWeekView = viewType === 'week'
  const isTodoPicker = callViewType === 'todo'
  const monthFirstDate = dateUtils.getMonthViewFirstDate(date)
  const startDate = moment(monthFirstDate).startOf('day')
  const today = moment().startOf('day')
  const focused = moment(focusedDate).startOf('day')

  if (limitDate.startDate == null) {
    limitDate.startDate = dateUtils.LIMIT_DATE.startDate
  }
  if (limitDate.endDate == null) {
    limitDate.endDate = dateUtils.LIMIT_DATE.endDate
  }

  const weeks: DayInfo[][] = []
  let days: DayInfo[] = []
  let day = startDate

  // 요일 헤더
  let weekDays: string[] = []
  for (let i = 0; i < 7; i++) {
    weekDays.push(moment.weekdaysShort(day.weekday()).charAt(0))
    day = moment(day).add(1, 'day')
  }

  day = startDate
  // 6주치 날짜 생성 (42일)
  for (let week = 0; week < 6; week++) {
    days = []
    for (let i = 0; i < 7; i++) {
      const currentDate = moment(day)
      const isCurrentMonth = currentDate.month() === moment(date).month()
      const isInLimitDate =
        currentDate.isSameOrAfter(limitDate.startDate) &&
        currentDate.isSameOrBefore(limitDate.endDate)
      const isToday = currentDate.isSame(today, 'day')
      const isFocused = currentDate.isSame(focused, 'day')

      days.push({
        date: currentDate,
        dayOfMonth: currentDate.date(),
        isCurrentMonth,
        isToday,
        isFocused,
        isInLimitDate,
      })

      day = moment(day).add(1, 'day')
    }
    weeks.push(days)
  }

  return (
    <div className={classNames(styles.monthGrid, isTodoPicker ? styles.todoPickerGrid : '')}>
      <div className={classNames(styles.weekDays, isTodoPicker ? styles.todoPickerWeekDays : '')}>
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={classNames(styles.weekDay, isTodoPicker ? styles.todoPickerWeekDay : '')}
          >
            {day}
          </div>
        ))}
      </div>
      <div className={styles.days}>
        {weeks.map((week, weekIndex) => (
          <div
            key={weekIndex}
            className={classNames(
              styles.week,
              isWeekView && week.findIndex((day) => day.isFocused) > 0 ? styles.focusedWeek : '',
              isWeekView ? styles.weekView : '',
            )}
          >
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className={classNames(
                  styles.day,
                  day.isCurrentMonth && day.isInLimitDate ? styles.currentMonth : styles.otherMonth,
                  day.isToday ? styles.today : '',
                  !isWeekView && !day.isToday && day.isFocused ? styles.focused : '',
                  isWeekView ? styles.weekView : '',
                  isTodoPicker ? styles.todoPickerDay : '',
                )}
                onClick={() => {
                  if (day.isInLimitDate) {
                    onDateClick(day.date)
                  }
                }}
              >
                {hasEventIndexes &&
                  hasEventIndexes.find((index) => index.index === weekIndex * 7 + dayIndex) && (
                    <div
                      className={`${styles.eventDot} ${day.isToday ? styles.eventDotToday : ''}`}
                    />
                  )}
                {day.dayOfMonth}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default MonthGrid
